import type { CSSProperties } from 'react';
import { useReducedMotion } from '../lib/useReducedMotion.js';

/**
 * Rare celestial shooting stars behind the UI.
 *
 * Frequency is an emergent property, not a fixed loop: N streaks each sit idle
 * for most of a long, prime-ish cycle and flash for well under a second. With
 * these values the perceived rate lands around one every 4–8 seconds, and
 * because no two cycles share a period the pattern never visibly repeats.
 *
 * Each streak differs in origin, angle, length, brightness and travel time.
 * They are confined to the upper/middle band so they never cross the card stack.
 */

function rng(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (Math.imul(s, 1103515245) + 12345) & 0x7fffffff;
    return s / 0x7fffffff;
  };
}

const R = (n: number): number => Math.round(n * 100) / 100;

interface Streak {
  top: number;
  left: number;
  dur: number;
  delay: number;
  len: number;
  angle: number;
  dx: number;
  dy: number;
  peak: number;
  thick: number;
}

/**
 * 9 streaks × ~54s average cycle ≈ one visible streak every ~6s, jittered by
 * the spread of durations and offset delays.
 */
/*
 * Four trajectory families, cycled so every one is represented rather than
 * emerging from a coin flip: shallow and steep falls in BOTH directions. All
 * are safely away from horizontal (min 24°), and none repeats another's angle.
 */
const FAMILIES = [
  { base: 34, spread: 12 }, // upper-left → lower-right, shallow
  { base: 62, spread: 14 }, // upper-left → lower-right, steep
  { base: 146, spread: 12 }, // upper-right → lower-left, shallow
  { base: 118, spread: 14 }, // upper-right → lower-left, steep
] as const;

const STREAKS: Streak[] = (() => {
  const rnd = rng(0x7c2ab9);
  return Array.from({ length: 9 }, (_, i) => {
    const fam = FAMILIES[i % FAMILIES.length];
    const angle = R(fam.base + (rnd() - 0.5) * fam.spread);
    const rad = (angle * Math.PI) / 180;
    const travel = R(160 + rnd() * 180);
    const leftward = angle > 90;
    const bright = rnd() < 0.22; // a minority read noticeably brighter
    return {
      top: R(2 + rnd() * 44), // upper / middle celestial band only
      left: R(leftward ? 48 + rnd() * 48 : 3 + rnd() * 49),
      dur: R(38 + rnd() * 34), // 38–72s cycles, all mutually out of phase
      delay: R(-rnd() * 72),
      len: R(48 + rnd() * 58),
      angle,
      // Travel direction matches the tail's angle exactly — no fudge factor, so
      // the streak reads as one coherent meteor rather than a skewed bar.
      dx: R(Math.cos(rad) * travel),
      dy: R(Math.sin(rad) * travel),
      peak: R(bright ? 0.85 + rnd() * 0.15 : 0.45 + rnd() * 0.3),
      thick: bright ? 1.5 : 1,
    };
  });
})();

export function ShootingStars(): JSX.Element | null {
  const reduced = useReducedMotion();
  if (reduced) return null; // a still sky is the reduced-motion composition

  return (
    <div className="shooting-stars" aria-hidden="true">
      {STREAKS.map((s, i) => (
        <span
          key={i}
          className="shooting-star"
          style={
            {
              top: `${s.top}%`,
              left: `${s.left}%`,
              '--dx': `${s.dx}px`,
              '--dy': `${s.dy}px`,
              '--len': `${s.len}px`,
              '--angle': `${s.angle}deg`,
              '--peak': s.peak,
              '--thick': `${s.thick}px`,
              animationDuration: `${s.dur}s`,
              animationDelay: `${s.delay}s`,
            } as CSSProperties
          }
        />
      ))}
    </div>
  );
}
